import * as React from "react";
import { cn } from "@/lib/utils";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

type AnimationVariant = 
  | "fade-up" 
  | "fade-down" 
  | "scale" 
  | "blur" 
  | "slide-left" 
  | "slide-right" 
  | "fade-left" 
  | "fade-right"
  | "zoom-in"
  | "rotate-in"
  | "flip-up"
  | "reveal";

interface AnimatedSectionProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  variant?: AnimationVariant;
  animation?: AnimationVariant; // Alias for variant
  delay?: number;
  duration?: number;
  threshold?: number;
  as?: "section" | "div" | "article" | "span";
  staggerChildren?: boolean;
  staggerDelay?: number;
}

const variantClasses: Record<AnimationVariant, { hidden: string; visible: string }> = {
  "fade-up": {
    hidden: "opacity-0 translate-y-6",
    visible: "opacity-100 translate-y-0",
  },
  "fade-down": {
    hidden: "opacity-0 -translate-y-6",
    visible: "opacity-100 translate-y-0",
  },
  "scale": {
    hidden: "opacity-0 scale-95",
    visible: "opacity-100 scale-100",
  },
  "blur": {
    hidden: "opacity-0 blur-sm scale-98",
    visible: "opacity-100 blur-0 scale-100",
  },
  "slide-left": {
    hidden: "opacity-0 translate-x-8",
    visible: "opacity-100 translate-x-0",
  },
  "slide-right": {
    hidden: "opacity-0 -translate-x-8",
    visible: "opacity-100 translate-x-0",
  },
  "fade-left": {
    hidden: "opacity-0 translate-x-6",
    visible: "opacity-100 translate-x-0",
  },
  "fade-right": {
    hidden: "opacity-0 -translate-x-6",
    visible: "opacity-100 translate-x-0",
  },
  "zoom-in": {
    hidden: "opacity-0 scale-90",
    visible: "opacity-100 scale-100",
  },
  "rotate-in": {
    hidden: "opacity-0 rotate-3 scale-95",
    visible: "opacity-100 rotate-0 scale-100",
  },
  "flip-up": {
    hidden: "opacity-0 [transform:perspective(600px)_rotateX(10deg)]",
    visible: "opacity-100 [transform:perspective(600px)_rotateX(0deg)]",
  },
  "reveal": {
    hidden: "opacity-0 [clip-path:inset(0_100%_0_0)]",
    visible: "opacity-100 [clip-path:inset(0_0%_0_0)]",
  },
};

export const AnimatedSection = ({
  children,
  className,
  variant = "fade-up",
  animation,
  delay = 0,
  duration = 700,
  threshold = 0.1,
  as: Component = "section",
  staggerChildren = false,
  staggerDelay = 100,
  ...props
}: AnimatedSectionProps) => {
  const { ref, isInView } = useScrollAnimation({ threshold, triggerOnce: true });
  const effectiveVariant = animation || variant;
  const classes = variantClasses[effectiveVariant];

  return (
    <Component
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn(
        "transition-all ease-out",
        isInView ? classes.visible : classes.hidden,
        className
      )}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
      }}
      {...props}
    >
      {staggerChildren
        ? React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<{ style?: React.CSSProperties; className?: string }>, {
                style: {
                  ...(child.props as { style?: React.CSSProperties }).style,
                  transitionDelay: `${delay + index * staggerDelay}ms`,
                },
                className: cn(
                  (child.props as { className?: string }).className,
                  "transition-all ease-out",
                  isInView ? classes.visible : classes.hidden
                ),
              });
            }
            return child;
          })
        : children}
    </Component>
  );
};

// Utility hook for staggered child animations
export function useStaggeredChildren(isInView: boolean, count: number, baseDelay = 0, staggerDelay = 100) {
  return Array.from({ length: count }, (_, i) => ({
    className: cn(
      "transition-all duration-500 ease-out",
      isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
    ),
    style: { transitionDelay: `${baseDelay + i * staggerDelay}ms` },
  }));
}

export default AnimatedSection;
